import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Briefcase, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  ApiError,
  publicApi,
  RateLimitError,
  type JobSummary,
  type JobsListEnvelope,
} from '../lib/apiClient';
import { useMasterData } from '../context/MasterDataContext';
import { useAppliedJobs } from '../context/AppliedJobsContext';
import { useAuth } from '../context/AuthContext';
import { RateLimitBanner } from '../components/RateLimitBanner';
import { usePageTitle } from '../lib/usePageTitle';
import { formatRelativeTime } from '../lib/relativeTime';
import './job-seekers.css';

const PAGE_SIZE = 10;
const TICKER_SPEED_PX_PER_SECOND = 100;

const JobTicker = memo(function JobTicker({ titles }: { titles: string[] }) {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const keepSpeedConsistent = () => {
      track.style.animationDuration = `${track.scrollWidth / 2 / TICKER_SPEED_PX_PER_SECOND}s`;
    };
    const resizeObserver = new ResizeObserver(keepSpeedConsistent);

    keepSpeedConsistent();
    resizeObserver.observe(track);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className="qh-jobseekers__ticker" aria-hidden="true">
      <div ref={trackRef} className="qh-jobseekers__ticker-track">
        {[...titles, ...titles].map((title, index) => (
          <span key={`${title}-${index}`}>{title}</span>
        ))}
        {titles.length === 0 && (
          <>
            <span>Roles across USA, Canada, India and Thailand</span>
            <span>New openings added regularly</span>
            <span>Roles across USA, Canada, India and Thailand</span>
            <span>New openings added regularly</span>
          </>
        )}
      </div>
    </div>
  );
});

export function JobSeekersPage() {
  usePageTitle('Find your next role - QuantumHire');
  const [params, setParams] = useSearchParams();
  const { candidate } = useAuth();
  const masterData = useMasterData();
  const dropdowns = masterData.data;

  const filters = useMemo(
    () => ({
      q: params.get('q') ?? '',
      region: params.get('region') ?? '',
      job_category: params.get('job_category') ?? '',
      employment_type: params.get('employment_type') ?? '',
      work_arrangement: params.get('work_arrangement') ?? '',
      experience_level: params.get('experience_level') ?? '',
      page: Number(params.get('page') ?? 1) || 1,
    }),
    [params],
  );

  const [searchInput, setSearchInput] = useState(filters.q);
  const [result, setResult] = useState<JobsListEnvelope | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => setSearchInput(filters.q), [filters.q]);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);

    publicApi
      .listJobs({
        q: filters.q || undefined,
        region: filters.region || undefined,
        job_category: filters.job_category || undefined,
        employment_type: filters.employment_type || undefined,
        work_arrangement: filters.work_arrangement || undefined,
        experience_level: filters.experience_level || undefined,
        page: filters.page,
        page_size: PAGE_SIZE,
      })
      .then((env) => {
        if (!alive) return;
        setResult(env);
        setRateLimited(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof RateLimitError) setRateLimited(true);
        else if (err instanceof ApiError) setError(err.message);
        else setError('Could not load jobs. Try again in a moment.');
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [
    filters.q,
    filters.region,
    filters.job_category,
    filters.employment_type,
    filters.work_arrangement,
    filters.experience_level,
    filters.page,
  ]);

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next, { replace: false });
  }

  function clearAll() {
    setSearchInput('');
    setParams(new URLSearchParams(), { replace: false });
  }

  function changePage(nextPage: number) {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next, { replace: false });
    document.querySelector('.qh-jobseekers__listings')?.scrollIntoView({ behavior: 'smooth' });
  }

  const total = result?.meta.total ?? 0;
  const totalPages = result?.meta.total_pages ?? 1;
  const hasFilters = Boolean(
    filters.q ||
      filters.region ||
      filters.job_category ||
      filters.employment_type ||
      filters.work_arrangement ||
      filters.experience_level,
  );
  const tickerTitles = useMemo(() => result?.data.map((job) => job.title) ?? [], [result]);

  return (
    <main className="qh-jobseekers">
      <JobTicker titles={tickerTitles} />

      <header className="qh-jobseekers__hero">
        <JobSeekersHeroBackground />
        <div className="qh-jobseekers__hero-inner">
          <div className="qh-jobseekers__eyebrow">
            <span className="qh-jobseekers__live-dot" /> Live openings, updated daily
          </div>
          <h1>
            Find work that&apos;s actually
            <br />
            <em>matched</em> to you.
          </h1>
          <p className="qh-jobseekers__lede">
            Every role below is being filled by a QuantumHire desk right now. Search, filter, and
            apply directly - or submit your profile once and let new roles find you.
          </p>

          <form
            className="qh-jobseekers__search-panel"
            onSubmit={(event) => {
              event.preventDefault();
              updateFilter('q', searchInput.trim());
            }}
          >
            <label className="qh-jobseekers__search-field">
              <span>Role / Keyword</span>
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="e.g. Product Manager, React, Sales"
              />
            </label>
            <label className="qh-jobseekers__search-field">
              <span>Location</span>
              <select value={filters.region} onChange={(event) => updateFilter('region', event.target.value)}>
                <option value="">All locations</option>
                {(dropdowns?.regions ?? []).map((region) => (
                  <option key={region.slug} value={region.slug}>
                    {region.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="qh-jobseekers__search-field">
              <span>Work mode</span>
              <select
                value={filters.work_arrangement}
                onChange={(event) => updateFilter('work_arrangement', event.target.value)}
              >
                <option value="">All modes</option>
                {(dropdowns?.workArrangements ?? []).map((mode) => (
                  <option key={mode.slug} value={mode.slug}>
                    {mode.name}
                  </option>
                ))}
              </select>
            </label>
            <button className="qh-jobseekers__search-submit" type="submit">
              Search roles
            </button>
          </form>

          <div className="qh-jobseekers__filter-pills">
            <label className="qh-jobseekers__filter-pill">
              <select
                value={filters.job_category}
                onChange={(event) => updateFilter('job_category', event.target.value)}
              >
                <option value="">All functions</option>
                {(dropdowns?.jobCategories ?? []).map((category) => (
                  <option key={category.slug} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="qh-jobseekers__filter-pill">
              <select
                value={filters.employment_type}
                onChange={(event) => updateFilter('employment_type', event.target.value)}
              >
                <option value="">Any employment type</option>
                {(dropdowns?.employmentTypes ?? []).map((type) => (
                  <option key={type.slug} value={type.slug}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="qh-jobseekers__filter-pill">
              <select
                value={filters.experience_level}
                onChange={(event) => updateFilter('experience_level', event.target.value)}
              >
                <option value="">Any experience level</option>
                {(dropdowns?.experienceLevels ?? []).map((level) => (
                  <option key={level.slug} value={level.slug}>
                    {level.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="qh-jobseekers__result-meta">
            <p>
              <strong>{total}</strong> open {total === 1 ? 'role' : 'roles'} match
            </p>
            {hasFilters && (
              <button type="button" onClick={clearAll}>
                Clear all filters
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="qh-jobseekers__listings" aria-live="polite">
        <div className="qh-jobseekers__wrap">
          <RateLimitBanner visible={rateLimited} onDismiss={() => setRateLimited(false)} />
          {masterData.status === 'error' && (
            <div className="qh-jobseekers__notice">
              {masterData.errorMessage ?? "Couldn't load filter options."}{' '}
              <button type="button" onClick={() => void masterData.refresh()}>
                Retry
              </button>
            </div>
          )}

          {error ? (
            <div className="qh-jobseekers__error">{error}</div>
          ) : isLoading && !result ? (
            <div className="qh-jobseekers__loading">
              <Loader2 aria-label="Loading jobs" />
            </div>
          ) : !result || result.data.length === 0 ? (
            <EmptyState hasFilters={hasFilters} onReset={clearAll} />
          ) : (
            <>
              <div className="qh-jobseekers__job-grid">
                {result.data.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
              {totalPages > 1 && (
                <nav className="qh-jobseekers__pagination" aria-label="Job results pages">
                  <button
                    type="button"
                    onClick={() => changePage(filters.page - 1)}
                    disabled={filters.page <= 1}
                  >
                    <ChevronLeft /> Previous
                  </button>
                  <span>
                    Page {filters.page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => changePage(filters.page + 1)}
                    disabled={filters.page >= totalPages}
                  >
                    Next <ChevronRight />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      <section className="qh-jobseekers__cv-band" id="cv">
        <h2>Don&apos;t see the right role yet?</h2>
        <p>
          Submit your profile once. Our matching engine keeps checking it against every new role we
          take on - you&apos;ll hear from a recruiter the moment there&apos;s a real fit.
        </p>
        <Link to={candidate ? '/dashboard/resumes' : '/signup'}>
          {candidate ? 'Manage your CV' : 'Submit your profile'}
        </Link>
        <div className="qh-jobseekers__cv-note">No spam. We only contact you about real matches.</div>
      </section>
      <section className="qh-jobseekers__tracks-preview">
  <div className="qh-jobseekers__tracks-wrap">
    <div className="qh-jobseekers__tracks-head">
      <span className="qh-jobseekers__tracks-tag">
        // Built for specific situations
      </span>

      <h2>In a specific career situation? We run dedicated tracks for that.</h2>

      <p>
        Not every job search looks the same - these two run alongside our standard listings.
      </p>
    </div>

    <div className="qh-jobseekers__tracks-grid">
      <Link
        to="/exfounders"
        className="qh-jobseekers__track-card qh-jobseekers__track-card--founders"
      >
        <div className="qh-jobseekers__track-bg" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 4v5h5" />
            <path d="M12 8v4l3 2" />
          </svg>
        </div>

        <div className="qh-jobseekers__track-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 4v5h5" />
            <path d="M12 8v4l3 2" />
          </svg>
        </div>

        <span className="qh-jobseekers__track-label">
          For ex-founders
        </span>

        <h3>Re-entering as an operator or leader</h3>

        <p>
          Positioning founder experience for the right seniority - a distinct search from a
          standard job application.
        </p>

        <span className="qh-jobseekers__track-link">
          Explore →
        </span>
      </Link>

      <Link
        to="/career-restart"
        className="qh-jobseekers__track-card qh-jobseekers__track-card--restart"
      >
        <div className="qh-jobseekers__track-bg" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3v4M12 3a9 9 0 1 1-7.4 3.9" />
            <path d="M12 3l3 2-3 2" />
          </svg>
        </div>

        <div className="qh-jobseekers__track-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3v4M12 3a9 9 0 1 1-7.4 3.9" />
            <path d="M12 3l3 2-3 2" />
          </svg>
        </div>

        <span className="qh-jobseekers__track-label">
          Career Restart
        </span>

        <h3>Returning after a career break</h3>

        <p>
          Weekly Saturday sessions for women re-entering the workforce - leader-led,
          role-focused, PAN India.
        </p>

        <span className="qh-jobseekers__track-link">
          Explore →
        </span>
      </Link>
    </div>
  </div>
</section>

      
    </main>
  );
}



function JobSeekersHeroBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const stableCanvas = canvas;
    const stableContext = context;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let animationFrame = 0;
    let width = 0;
    let height = 0;
    let nodes: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulse: number;
      highlighted: boolean;
    }> = [];

    function resize() {
      const pixelRatio = window.devicePixelRatio || 1;
      width = stableCanvas.clientWidth * pixelRatio;
      height = stableCanvas.clientHeight * pixelRatio;
      stableCanvas.width = width;
      stableCanvas.height = height;

      const nodeCount = Math.min(
        70,
        Math.floor((stableCanvas.clientWidth * stableCanvas.clientHeight) / 16000),
      );
      nodes = Array.from({ length: nodeCount }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25 * pixelRatio,
        vy: (Math.random() - 0.5) * 0.25 * pixelRatio,
        radius: (Math.random() * 1.6 + 0.8) * pixelRatio,
        pulse: Math.random() * Math.PI * 2,
        highlighted: Math.random() < 0.12,
      }));
    }

    function draw() {
      const pixelRatio = window.devicePixelRatio || 1;
      stableContext.clearRect(0, 0, width, height);

      for (let firstIndex = 0; firstIndex < nodes.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < nodes.length; secondIndex += 1) {
          const first = nodes[firstIndex];
          const second = nodes[secondIndex];
          const xDistance = first.x - second.x;
          const yDistance = first.y - second.y;
          const distance = Math.sqrt(xDistance * xDistance + yDistance * yDistance);
          const maximumDistance = 150 * pixelRatio;

          if (distance < maximumDistance) {
            const alpha = (1 - distance / maximumDistance) * 0.14;
            stableContext.strokeStyle = `rgba(11, 92, 34, ${alpha})`;
            stableContext.lineWidth = 1;
            stableContext.beginPath();
            stableContext.moveTo(first.x, first.y);
            stableContext.lineTo(second.x, second.y);
            stableContext.stroke();
          }
        }
      }

      nodes.forEach((node) => {
        if (!reduceMotion) {
          node.x += node.vx;
          node.y += node.vy;
          if (node.x < 0 || node.x > width) node.vx *= -1;
          if (node.y < 0 || node.y > height) node.vy *= -1;
          node.pulse += 0.02;
        }

        const glow = node.highlighted ? (Math.sin(node.pulse) + 1) / 2 : 0.15;
        stableContext.beginPath();
        stableContext.arc(
          node.x,
          node.y,
          node.radius + (node.highlighted ? glow * 2 * pixelRatio : 0),
          0,
          Math.PI * 2,
        );
        stableContext.fillStyle = node.highlighted
          ? `rgba(28, 170, 60, ${0.55 + glow * 0.45})`
          : 'rgba(20, 21, 15, 0.22)';
        stableContext.fill();
      });

      if (!reduceMotion) animationFrame = window.requestAnimationFrame(draw);
    }

    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (reduceMotion) draw();
    });
    resizeObserver.observe(canvas);
    resize();
    draw();

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return <canvas ref={canvasRef} className="qh-jobseekers__hero-canvas" aria-hidden="true" />;
}

function JobCard({ job }: { job: JobSummary }) {
  const { isJobApplied } = useAppliedJobs();
  const hasApplied = job.has_applied || isJobApplied(job.id);
  const location = [job.cities.map((city) => city.name).join(', '), job.region.name]
    .filter(Boolean)
    .join(', ');

  return (
    <article className="qh-jobseekers__job-card">
      <div className="qh-jobseekers__job-main">
        <div className="qh-jobseekers__tag-row">
          <span className="qh-jobseekers__job-function">
            {job.employment_type?.name ?? job.experience_level?.name ?? 'Open role'}
          </span>
          {hasApplied && <span className="qh-jobseekers__job-applied">Applied</span>}
        </div>
        <h2>{job.title}</h2>
        <div className="qh-jobseekers__job-meta">
          <span>{location}</span>
          {job.work_arrangement && <span>{job.work_arrangement.name}</span>}
          {job.published_at && <span>Posted {formatRelativeTime(job.published_at)}</span>}
        </div>
      </div>
      <div className="qh-jobseekers__job-side">
        {job.salary.visible && (
          <span className="qh-jobseekers__salary">
            {job.salary.currency?.symbol ?? ''}
            {formatNumber(job.salary.min)}-{job.salary.currency?.symbol ?? ''}
            {formatNumber(job.salary.max)}
            {job.salary.pay_period ? ` / ${job.salary.pay_period.name.toLowerCase()}` : ''}
          </span>
        )}
        <Link to={`/job-seekers/${job.slug}`} className="qh-jobseekers__apply">
          View job <span aria-hidden="true">-&gt;</span>
        </Link>
      </div>
    </article>
  );
}

function EmptyState({ hasFilters, onReset }: { hasFilters: boolean; onReset: () => void }) {
  return (
    <div className="qh-jobseekers__empty-state">
      <Briefcase aria-hidden="true" />
      <h2>{hasFilters ? 'No roles match that search right now.' : 'No jobs available yet.'}</h2>
      <p>
        {hasFilters
          ? "Clear your filters, or submit your profile - we'll match you the moment something opens."
          : "We're onboarding the first cohort of partner companies. Check back shortly."}
      </p>
      {hasFilters && (
        <button type="button" onClick={onReset}>
          Clear all filters
        </button>
      )}
    </div>
  );
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}
